import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HomeWeatherWidget from '../components/HomeWeatherWidget';
import { Card } from '../components/ui/card';
import { Lightbulb, Scale, Wrench, MapPin, TrendingUp, Anchor, Loader2, ArrowRight } from 'lucide-react';
import { logger } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback for legacy articles created before the subcategory field existed
const LEGACY_SLUG_CATEGORY_MAP = {
  'amenagement-bateau-optimiser-espace': 'idees',
  'cuisine-bord-recettes-astuces': 'vie-quotidienne',
  'innovations-nautisme-2026': 'nouveautes',
  'reglementation-plaisance-2026': 'juridique',
  'entretien-bateau-guide-complet': 'conseils',
  'mouillages-secrets-france': 'bons-plans',
};

const getSubcategory = (article) => {
  return article.subcategory || LEGACY_SLUG_CATEGORY_MAP[article.slug] || null;
};

const CATEGORIES = [
  {
    key: 'idees',
    icon: <Lightbulb size={32} />,
    title: "Idées & Inspirations",
    description: "Aménagements, déco, cuisine et optimisation de l'espace à bord",
    color: "from-yellow-500/20 to-yellow-600/10",
  },
  {
    key: 'nouveautes',
    icon: <TrendingUp size={32} />,
    title: "Nouveautés",
    description: "Les dernières innovations, produits et tendances du nautisme",
    color: "from-blue-500/20 to-blue-600/10",
  },
  {
    key: 'juridique',
    icon: <Scale size={32} />,
    title: "Juridique",
    description: "Réglementation, assurances, droits et obligations du plaisancier",
    color: "from-purple-500/20 to-purple-600/10",
  },
  {
    key: 'conseils',
    icon: <Wrench size={32} />,
    title: "Conseils & Astuces",
    description: "Trucs de pros, entretien, réparations, bricolage nautique",
    color: "from-green-500/20 to-green-600/10",
  },
  {
    key: 'bons-plans',
    icon: <MapPin size={32} />,
    title: "Bons Plans",
    description: "Mouillages secrets, ports économiques, réductions et offres",
    color: "from-red-500/20 to-red-600/10",
  },
  {
    key: 'vie-quotidienne',
    icon: <Anchor size={32} />,
    title: "Vie Quotidienne",
    description: "Cuisine à bord, météo, sécurité, vivre sur l'eau au quotidien",
    color: "from-cyan-500/20 to-cyan-600/10",
  },
];

const VieABord = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${API_URL}/api/articles`);
        if (response.ok) {
          const data = await response.json();
          // Keep articles that are either in VIE À BORD category OR have a known subcategory
          const vieArticles = data.filter(
            (a) => a.category === 'VIE À BORD' || getSubcategory(a)
          );
          setArticles(vieArticles);
        }
      } catch (err) {
        logger.warn('API non disponible pour Vie à bord — fallback statique', err);
      }
      setLoading(false);
    };
    fetchArticles();
  }, []);

  const getArticlesForCategory = (categoryKey) => {
    return articles.filter((a) => getSubcategory(a) === categoryKey);
  };

  return (
    <>
      <Helmet>
        <title>Vie à Bord - Conseils, Astuces & Bons Plans | validatedboat</title>
        <meta name="description" content="Découvrez nos conseils pour la vie à bord : idées d'aménagement, nouveautés nautiques, réglementation, astuces d'entretien et bons plans pour naviguer sereinement." />
        <meta name="keywords" content="vie à bord bateau, conseils nautiques, astuces bateau, réglementation maritime, bons plans navigation, entretien bateau" />
      </Helmet>

      <Header />

      <main className="min-h-screen bg-[#1a1c1b] pt-20">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-[#302f2c] to-[#1a1c1b]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-black text-4xl md:text-6xl leading-tight text-[#d9fb06] uppercase mb-6">
                Vie à Bord
              </h1>
              <p className="text-xl md:text-2xl text-[#dfddd6] mb-4">
                Tout pour profiter pleinement de votre bateau
              </p>
              <p className="text-lg text-[#888680]">
                Idées, nouveautés, juridique, conseils, astuces et bons plans pour les passionnés de nautisme
              </p>
            </div>
          </div>
        </section>

        {/* Weather Section */}
        <section className="py-8 md:py-12">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <HomeWeatherWidget />
          </div>
        </section>

        {/* Categories Grid */}
        <section className="py-12 md:py-16">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#d9fb06] uppercase mb-8 text-center">
              Explorez par thème
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {CATEGORIES.map((category) => {
                const catArticles = getArticlesForCategory(category.key);
                return (
                  <a
                    key={category.key}
                    href={`#cat-${category.key}`}
                    data-testid={`category-card-${category.key}`}
                    className="block"
                  >
                    <Card
                      className={`bg-gradient-to-br ${category.color} border border-[#3f4816]/50 hover:border-[#d9fb06]/50 transition-all duration-300 hover:transform hover:scale-[1.02] p-6 h-full`}
                    >
                      <div className="text-[#d9fb06] mb-4">{category.icon}</div>
                      <h3 className="text-xl font-bold text-[#d9fb06] mb-2 uppercase">
                        {category.title}
                      </h3>
                      <p className="text-[#888680] mb-4 text-sm">{category.description}</p>
                      <p className="text-[#d9fb06] text-sm font-semibold">
                        {catArticles.length} article{catArticles.length > 1 ? 's' : ''}
                      </p>
                    </Card>
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        {/* Articles grouped by category */}
        <section className="py-12 md:py-16 bg-[#1a1c1b]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-10 h-10 text-[#d9fb06] animate-spin" />
              </div>
            ) : (
              CATEGORIES.map((category) => {
                const catArticles = getArticlesForCategory(category.key);
                if (catArticles.length === 0) return null;
                return (
                  <div key={category.key} id={`cat-${category.key}`} className="mb-16 scroll-mt-24">
                    <div className="flex items-center gap-3 mb-6 pb-3 border-b border-[#3f4816]/50">
                      <div className="text-[#d9fb06]">{category.icon}</div>
                      <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase">
                        {category.title}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {catArticles.map((article) => (
                        <Link
                          key={article.slug}
                          to={`/blog/${article.slug}`}
                          data-testid={`vieabord-article-${article.slug}`}
                          className="group"
                        >
                          <Card className="bg-[#26282a] border border-[#3f4816]/50 hover:border-[#d9fb06]/50 transition-all duration-300 overflow-hidden h-full flex flex-col">
                            <div className="aspect-video overflow-hidden">
                              <img
                                src={article.image}
                                alt={article.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                            <div className="p-5 flex flex-col flex-1">
                              <h3 className="text-lg font-bold text-[#dfddd6] group-hover:text-[#d9fb06] transition-colors mb-2 line-clamp-2">
                                {article.title}
                              </h3>
                              <p className="text-[#888680] text-sm mb-4 line-clamp-3 flex-1">
                                {article.excerpt}
                              </p>
                              <div className="flex items-center justify-between text-sm pt-3 border-t border-[#3f4816]/50">
                                <span className="text-[#888680]">{article.read_time}</span>
                                <span className="text-[#d9fb06] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                  Lire <ArrowRight size={14} />
                                </span>
                              </div>
                            </div>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              })
            )}

            {/* Empty state for categories without articles */}
            {!loading && CATEGORIES.filter((c) => getArticlesForCategory(c.key).length === 0).length > 0 && (
              <div className="mt-8 p-6 bg-[#26282a] border border-[#3f4816]/50 rounded-lg text-center">
                <p className="text-[#dfddd6]">
                  De nouveaux articles arrivent régulièrement dans les autres catégories.{' '}
                  <Link to="/blog" className="text-[#d9fb06] underline">
                    Voir tout le blog
                  </Link>
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter / Suggestion */}
        <section className="py-16 md:py-24">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <div className="bg-gradient-to-r from-[#3f4816]/30 to-[#302f2c] rounded-2xl p-8 md:p-12">
              <div className="max-w-2xl mx-auto text-center">
                <h3 className="text-2xl md:text-3xl font-bold text-[#d9fb06] mb-4">
                  Un sujet vous intéresse ?
                </h3>
                <p className="text-[#dfddd6] mb-6">
                  Vous avez une question, une idée d'article ou un bon plan à partager ?
                  Contactez-nous et nous l'ajouterons à notre liste !
                </p>
                <Link
                  to="/#contact"
                  data-testid="vieabord-contact-btn"
                  className="inline-flex items-center justify-center bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e205] font-bold rounded-full px-8 py-3 uppercase transition-colors"
                >
                  Nous contacter
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default VieABord;
